import type { InputState } from "../input/input";
import { harvestAt, plantAt, tickCrops } from "./ecology";
import { tickStrike } from "./combat";
import { killPlayer, recoverCache, wakeAfterDeath } from "./death";
import { descendFurther, enterHollow, extractHollow, pickupNear, say, tickEnemies } from "./hollow";
import { chronicle, enterHome, leaveHome, placeOnBench, stashAll } from "./home";
import { addItem, countItem, takeItem } from "./inventory";
import { nearestNpc, seedNpcs, talkTo, tickNpcs } from "./npcs";
import { standingOn, tickPlayer } from "./player";
import { tickSystems } from "./reactions";
import { advanceTime } from "./time";
import { SAVE_VERSION, Tile, type WorldState } from "./types";
import { tickWeather } from "./weather";
import { buildOverworld } from "../world/overworldMap";

export function createWorld(seed = 0xc1d3): WorldState {
  const overworld = buildOverworld();
  return {
    seed,
    version: SAVE_VERSION,
    mode: "title",
    prevMode: "title",
    timeSec: 6.4 * 3600,
    weather: "overcast",
    weatherT: 240,
    season: "thaw",
    player: {
      x: overworld.wake.x,
      y: overworld.wake.y,
      vx: 0,
      vy: 0,
      facing: { x: -1, y: 0 },
      hp: 100,
      hpMax: 100,
      stamina: 1,
      staminaMax: 1,
      hunger: 0.15,
      injury: 0,
      dashT: 0,
      dashCd: 0,
      strikeT: 0,
      strikePhase: "idle",
      strikeLanded: false,
      lantern: false,
      oil: 1,
      invuln: 0,
      overworldX: overworld.wake.x,
      overworldY: overworld.wake.y,
    },
    bag: [
      { id: "ration", n: 2 },
      { id: "seed_root", n: 3 },
      { id: "lantern_oil", n: 1 },
    ],
    safeBag: [],
    overworld,
    home: {
      dirt: 0.08,
      blood: 0,
      furniture: ["bed", "stove", "chest"],
      bench: [null, null, null],
      chest: [{ id: "bandage", n: 1 }],
      trophies: [],
      visits: 0,
    },
    hollow: null,
    npcs: seedNpcs(),
    factions: [
      { id: "wardens", stance: 0.1, control: 0.4 },
      { id: "survey", stance: 0.2, control: 0.2 },
      { id: "fence", stance: 0.3, control: 0.15 },
      { id: "ember", stance: -0.2, control: 0.05 },
    ],
    memory: [
      { x: 44.2, y: 18.4, kind: "blood", age: 0, life: 1, hue: 0.04, layer: "overworld" },
      { x: 43.1, y: 19.3, kind: "blood", age: 0, life: 1, hue: 0.03, layer: "overworld" },
      { x: 41.6, y: 21.1, kind: "blood", age: 0, life: 1, hue: 0.02, layer: "overworld" },
      { x: 40.4, y: 22.4, kind: "foot", age: 0, life: 1, hue: 0.08, layer: "overworld" },
      { x: 46.2, y: 17.2, kind: "blood", age: 0, life: 1, hue: 0.05, layer: "overworld" },
      { x: 49.8, y: 15.4, kind: "foot", age: 0, life: 1, hue: 0.1, layer: "overworld" },
    ],
    caches: [],
    log: [{ t: 6.4 * 3600, text: "June did not open her door.", kind: "story" }],
    knowledge: {
      wellSeen: false,
      hollowEntered: false,
      deepest: 0,
      juneMissing: true,
      plates: 0,
      extractedOnce: false,
    },
    msg: "Dawn on the county road. Blood on the grass runs two ways: the well, and the trees.",
    msgT: 5,
    shake: 0,
    deaths: 0,
    extracts: 0,
  };
}

export function tickWorld(w: WorldState, dt: number, input: InputState): void {
  if (w.mode === "title") {
    if (input.confirm || input.interact) {
      w.mode = "overworld";
      say(w, "Wind off the creek. The well is already steaming.");
    }
    return;
  }
  if (w.mode === "death") {
    if (input.confirm || input.interact) wakeAfterDeath(w);
    return;
  }
  if (w.mode === "pause") {
    if (input.pause) w.mode = w.prevMode;
    return;
  }
  if (input.pause) {
    w.prevMode = w.mode;
    w.mode = "pause";
    return;
  }

  advanceTime(w, dt);
  tickWeather(w, dt);
  w.msgT = Math.max(0, w.msgT - dt);
  w.shake = Math.max(0, w.shake - dt * 1.8);
  for (const d of w.memory) d.age += dt;

  if (w.mode === "overworld" || w.mode === "home" || w.mode === "hollow") {
    const hung = w.player.hunger;
    tickPlayer(w, dt, input);
    tickStrike(w, dt, input);
    if (w.mode === "hollow" && hung <= 0.9 && w.player.hunger > 0.9) {
      say(w, "Your gut folds. The Hollow will take the rest.");
    }
  }
  if (w.mode === "overworld") {
    tickNpcs(w, dt);
    tickCrops(w, dt);
    if (input.interact) interactOverworld(w);
  } else if (w.mode === "home") {
    if (input.interact) interactHome(w);
  } else if (w.mode === "hollow") {
    tickEnemies(w, dt);
    if (input.interact) interactHollow(w);
  }

  if (w.player.hp <= 0) killPlayer(w);

  // Cross-system layer. Runs last so it sees the finished frame.
  tickSystems(w, dt);
}

function interactOverworld(w: WorldState): void {
  if (recoverCache(w)) return;
  const t = standingOn(w);
  const px = w.player.x;
  const py = w.player.y;
  // Well before talk — Pike stations the mouth and would otherwise eat E.
  if (t === Tile.Well || Math.hypot(px - w.overworld.well.x - 0.5, py - w.overworld.well.y - 0.5) < 1.2) {
    w.knowledge.wellSeen = true;
    enterHollow(w, 1);
    return;
  }
  const npc = nearestNpc(w);
  if (npc && !npc.memory.missing) {
    say(w, talkTo(w, npc));
    return;
  }
  if (Math.hypot(px - w.overworld.homeDoor.x - 0.5, py - w.overworld.homeDoor.y - 0.2) < 1.1) {
    enterHome(w);
    say(w, chronicle(w));
    return;
  }
  const crop = w.overworld.crops.find((c) => Math.hypot(c.x + 0.5 - px, c.y + 0.5 - py) < 0.9);
  if (crop && crop.stage >= 3) {
    harvestAt(w, crop.x, crop.y);
    say(w, "You pull what the soil would give.");
    return;
  }
  if (crop && crop.stage < 3) {
    say(w, crop.water < 0.2 ? "The bed is thirsty." : "Not yet.");
    return;
  }
  const soilNear = t === Tile.Soil;
  if (soilNear && countItem(w.bag, "seed_root") > 0) {
    if (plantAt(w, px, py, "root") && takeItem(w.bag, "seed_root", 1)) {
      say(w, "Seed in the east bed.");
      return;
    }
  }
  if (Math.hypot(px - w.overworld.store.x - 0.5, py - w.overworld.store.y - 1.5) < 1.4) {
    if (countItem(w.bag, "scrap") >= 2) {
      takeItem(w.bag, "scrap", 2);
      addItem(w.bag, { id: "ration", n: 1 });
      addItem(w.bag, { id: "lantern_oil", n: 1 });
      say(w, "Mara trades scrap for oil and a jar.");
    } else {
      say(w, "Mara: \"Bring scrap or cinder. I don't run a charity.\"");
    }
    return;
  }
  const p = w.overworld.pois;
  if (near(px, py, p.june, 1.2)) {
    say(w, "June's chair still rocks. Blood leaves the porch in two directions.");
    return;
  }
  if (near(px, py, p.pump, 1.4)) {
    if (addItem(w.bag, { id: "scrap", n: 1 })) say(w, "The pump house is drowned. You twist a bracket free. The gauge ticks without water.");
    else say(w, "The gauge ticks without water.");
    return;
  }
  if (near(px, py, p.stake, 1.1)) {
    if (!w.knowledge.plates) {
      addItem(w.bag, { id: "survey_plate", n: 1 });
      w.knowledge.plates = 1;
      say(w, "A Survey stake. The plate says the well is a dry hole. The well is not dry.");
    } else {
      say(w, "The stake has nothing left to lie about.");
    }
    return;
  }
  if (near(px, py, p.mouth, 1.3)) {
    if (w.extracts >= 1 || w.knowledge.deepest >= 1) {
      say(w, "The south ring is open. The dirt is no longer just warm.");
      enterHollow(w, Math.max(2, w.knowledge.deepest || 2));
    } else {
      say(w, "A stone ring in the south grass. Sealed. The dirt here is warm.");
    }
    return;
  }
  say(w, "Nothing to put your hands on.");
}

function near(px: number, py: number, v: { x: number; y: number } | undefined, r: number): boolean {
  if (!v) return false;
  return Math.hypot(px - v.x - 0.5, py - v.y - 0.5) < r;
}

function interactHome(w: WorldState): void {
  if (w.player.y > 7.6) {
    leaveHome(w);
    return;
  }
  if (w.player.x > 7 && placeOnBench(w)) {
    say(w, "It sits on the bench and does not look like an object.");
    return;
  }
  if (w.player.x < 5) {
    const n = stashAll(w);
    say(w, n ? `You put ${n} things in the chest.` : "The chest already holds what you own.");
    return;
  }
  if (eatRation(w, "stove")) return;
  say(w, chronicle(w));
}

function interactHollow(w: WorldState): void {
  if (!w.hollow) return;
  if (recoverCache(w)) return;
  if (pickupNear(w)) return;
  const t = standingOn(w);
  if (t === Tile.Well || Math.hypot(w.player.x - w.hollow.start.x, w.player.y - w.hollow.start.y) < 1.0) {
    extractHollow(w);
    return;
  }
  if (t === Tile.Exit && w.hollow.down) {
    descendFurther(w);
    return;
  }
  if (eatOrBandage(w)) return;
  say(w, "The walls listen.");
}

/** Cold ration first when starving — food is why you farm. */
export function eatOrBandage(w: WorldState): boolean {
  if (w.player.oil < 0.25 && refillOil(w)) return true;
  if (w.player.hunger > 0.55 && eatRation(w, "cold")) return true;
  if (w.player.hp < w.player.hpMax - 8 && countItem(w.bag, "bandage") > 0) {
    takeItem(w.bag, "bandage", 1);
    w.player.hp = Math.min(w.player.hpMax, w.player.hp + 28);
    w.player.injury = Math.max(0, w.player.injury - 0.2);
    say(w, "Linen pulls tight.");
    return true;
  }
  return eatRation(w, "cold");
}

export function eatRation(w: WorldState, kind: "stove" | "cold"): boolean {
  if (countItem(w.bag, "ration") <= 0 || w.player.hunger <= 0.2) return false;
  takeItem(w.bag, "ration", 1);
  w.player.hunger = Math.max(0, w.player.hunger - (kind === "stove" ? 0.45 : 0.32));
  if (kind === "stove") {
    w.player.hp = Math.min(w.player.hpMax, w.player.hp + 12);
    say(w, "The stove ticks. You eat.");
  } else {
    say(w, "You eat standing. It does not taste like home.");
  }
  return true;
}

export function refillOil(w: WorldState): boolean {
  if (countItem(w.bag, "lantern_oil") <= 0 || w.player.oil >= 0.95) return false;
  takeItem(w.bag, "lantern_oil", 1);
  w.player.oil = Math.min(1, w.player.oil + 0.7);
  if (w.mode === "hollow") w.player.lantern = true;
  say(w, "You refill the lantern. The dark steps back.");
  return true;
}
